'use client';

import { useCalculatedAttributeResolver } from '@hyphengroup/cms-components/calculatedAttributes/useCalculatedAttributeResolver';
import type { PlanTierCardData } from '@hyphengroup/cms-components/cms-blocks/PlanTierSelector/types';
import {
  ATTR_NAMES,
  getAttrFromProduct,
} from '@hyphengroup/cms-components/types/attrNames';
import type { CmsProduct } from '@hyphengroup/cms-components/types/cmsProduct';
import { byPremiumAscending, tiPremiumOf } from './tiPremium';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import type { CmsBaseComponent } from '$/(cms)/_modules/cmsService/types';
import type {
  CmsPlanTier,
  CmsPlanTierSelectorComponent,
} from '$/(cms)/_modules/cmsService/types/components';
import { rendersTiProviderDetails } from '$/components/cms/components-internal/ProductDetails/rendersTiProviderDetails';
import {
  filterComponentsByType,
  findPlanTierSelector,
  isVisibleBlock,
} from '$/components/cms/helpers/componentTree';
import { useClientContext } from '$/modules/context/client';
import { tiCurrencyCodeOf } from '$/types/travelInsurance';

export type UseTiTiersResult = {
  /** Built + price-sorted tier cards for the active trip type. */
  tiers: PlanTierCardData[];
  /**
   * The tier shown as selected: the user's pick while it is in `tiers`; on a
   * page with a visible TI_PROVIDER ProductDetails, else the recommended tier,
   * else the pinned default, else the first (cheapest) once the search settles
   * (CMS-2054).
   */
  effectiveTierId: string | undefined;
  /**
   * The cheapest tier, set once a successful search shows it as the default
   * and nothing is pinned yet; the TI ProductDetails wrapper pins it so it
   * survives the next re-search (AC 3.5).
   */
  defaultTierToPin: string | undefined;
  pinDefaultTier: (id: string | undefined) => void;
  /**
   * Recommended, else the first tier, with no search or page gate: the M3
   * summary card's fallback while `effectiveTierId` is undefined.
   */
  defaultTierId: string | undefined;
  /** The `effectiveTierId` tier's card. */
  activeTier: PlanTierCardData | undefined;
  /** The `effectiveTierId` tier's product (fed to the TI ProductDetails + footer). */
  activeProduct: CmsProduct | undefined;
  /** The `effectiveTierId` tier's display name (footer "Apply for :product-name"). */
  activeTierName: string | undefined;
  recommendedBadgeLabel: string | undefined;
  setSelectedTier: (id?: string) => void;
  /** Raw trip type (used by M3b to reset the selection when it toggles). */
  activeTripType: string | undefined;
  isAnnual: boolean;
  /** ISO currency code for the price line (e.g. SGD / HKD) — the block formats it. */
  currencyCode: string;
  searchCompleted: boolean;
};

const uidOf = (product: CmsProduct): string | undefined =>
  getAttrFromProduct('INSUREMO_PRODUCT_UID', product)?.value ?? undefined;

// A tier is pre-selected only where a visible TI_PROVIDER ProductDetails
// renders the TI provider details; a page pairing M3b with a FULL_PAGE
// ProductDetails keeps selection click-only (CMS-2054).
const hasTiProviderDetails = (components: CmsBaseComponent[] | undefined) =>
  filterComponentsByType(components, 'ProductDetailsComponent').some(
    (c) => rendersTiProviderDetails(c) && isVisibleBlock(c),
  );

/**
 * Shared tier-resolution for the TI provider page (CMS-1643 / CMS-1644):
 * resolves the active trip-type product set from the M3b component, prices each
 * tier through the calculated-attributes engine (`TI_PREMIUM_PRICE` from the
 * block's slot, i.e. the page quote), sorts by price, and exposes the page-wide
 * active tier + its product. All copy and formatting live in the block
 * (CMS-1864), so this hook is a pure data layer and renders no strings.
 *
 * Consumed by `PlanTierSelectorWrapper` (M3b), the TI nav tier dropdown, the
 * sticky footer, the TI ProductDetails section product and the M4 column
 * highlight — so they all read the same tier list and active tier. Pure
 * derivation + context reads (no effects): the trip-type reset stays in the M3b
 * wrapper so it fires once.
 */
export const useTiTiers = (
  component: CmsPlanTierSelectorComponent | undefined,
): UseTiTiersResult => {
  const context = useClientContext();
  const resolveCalculated = useCalculatedAttributeResolver();
  const {
    pageData: { travelQuotes, page },
    setSelectedTier,
    pinnedDefaultTier,
    setPinnedDefaultTier,
  } = usePageDataContext();

  const activeTripType = travelQuotes?.params?.insuranceType;
  const isAnnual = activeTripType === 'ANNUAL_PLAN';

  // ISO currency code shown under the price (e.g. SGD / HKD).
  const currencyCode = tiCurrencyCodeOf(context.brand);

  // The M3b component owns the tier list; when it is absent from the page the
  // TI ProductDetails renders nothing (documented coupling — the wrapper gates
  // on it), so an empty list here is the correct fallback.
  const sourceTiers: CmsPlanTier[] = component
    ? isAnnual
      ? component.productsAnnual
      : component.productsSingle
    : [];

  const searchCompleted = travelQuotes?.status === 'success';

  // Recommended plan + badge label are per list — use the pair matching the
  // active trip type (single / annual).
  const recommendedPlan = isAnnual
    ? component?.recommendedPlanAnnual
    : component?.recommendedPlanSingle;
  const recommendedBadgeLabel = isAnnual
    ? component?.recommendedBadgeLabelAnnual
    : component?.recommendedBadgeLabelSingle;
  const recommendedUid = recommendedPlan ? uidOf(recommendedPlan) : undefined;

  const tiers = sourceTiers
    .map(({ product, displayNameOverride }): PlanTierCardData | undefined => {
      const id = uidOf(product);
      // The UID is the tier's identity and the engine's quote key — skip without.
      if (!id) {
        return undefined;
      }

      // A tier whose premium isn't in the current quote result keeps its place
      // (price stays undefined) rather than being dropped — a later poll may
      // still return its premium.
      const result = resolveCalculated(ATTR_NAMES.TI_PREMIUM_PRICE, product);

      return {
        id,
        // `||` (not `??`) so an empty-string override or name falls through
        // to the next option instead of rendering a blank card.
        name:
          displayNameOverride || product.product_name || product.title || '',
        description: product.description ?? undefined,
        // ApplyCtaLink (in the block) resolves the CTA URL + label from the
        // product and forwards the current query string (UTM / trip criteria).
        product,
        isRecommended: !!recommendedUid && recommendedUid === id,
        price: tiPremiumOf(result),
        isPriceLoading: result?.state === 'pending',
      };
    })
    .filter((tier) => tier !== undefined)
    .sort((a, b) => byPremiumAscending(a.price, b.price));

  // Derived on every render, so a pick or pinned default that is not in the
  // active trip type's list (a Single <-> Annual toggle) falls back on its own.
  // A re-search never drops a tier: `tiers` is the CMS product list and an
  // unpriced tier keeps its place, so a pick survives a quote that has no
  // premium for it. Only TI_PROVIDER pages show a tier without a click;
  // `tiers[0]` waits for the search, as before that it is CMS order, not the
  // cheapest. Once a successful search shows the cheapest tier it is pinned, so
  // a later re-search neither hides nor swaps it (CMS-2054, AC 3.5).
  const selectedTierId = travelQuotes?.selectedTier;
  const searchSettled = !!travelQuotes && travelQuotes.status !== 'inprogress';
  const selectedInList = tiers.some((tier) => tier.id === selectedTierId)
    ? selectedTierId
    : undefined;
  // Looked up in the built list: a recommended plan missing from it must not
  // stand in for a tier.
  const recommendedTierId = tiers.find((tier) => tier.isRecommended)?.id;
  const defaultTierId = recommendedTierId ?? tiers[0]?.id;
  const pinnedInList = tiers.some((tier) => tier.id === pinnedDefaultTier)
    ? pinnedDefaultTier
    : undefined;
  const preselect = hasTiProviderDetails(page?.components);
  const effectiveTierId =
    selectedInList ??
    (preselect
      ? (recommendedTierId ??
        pinnedInList ??
        (searchSettled ? tiers[0]?.id : undefined))
      : undefined);
  // Only a successful search pins: a fail / timeout fallback is CMS order, not
  // the cheapest, so the next search may still replace it.
  const defaultTierToPin =
    preselect &&
    !selectedInList &&
    !recommendedTierId &&
    !pinnedInList &&
    travelQuotes?.status === 'success'
      ? tiers[0]?.id
      : undefined;
  const activeTier = tiers.find((tier) => tier.id === effectiveTierId);

  return {
    tiers,
    effectiveTierId,
    defaultTierToPin,
    pinDefaultTier: setPinnedDefaultTier,
    defaultTierId,
    activeTier,
    activeProduct: activeTier?.product,
    activeTierName: activeTier?.name,
    recommendedBadgeLabel,
    setSelectedTier,
    activeTripType,
    isAnnual,
    currencyCode,
    searchCompleted,
  };
};

/**
 * `useTiTiers` for blocks that read M3b's tiers from elsewhere on the page (M4,
 * M6). A hidden M3b still supplies the tiers (see `findPlanTierSelector`).
 */
export const usePageTiTiers = (): UseTiTiersResult & {
  planTierComponent: CmsPlanTierSelectorComponent | undefined;
} => {
  const {
    pageData: { page },
  } = usePageDataContext();
  const planTierComponent = findPlanTierSelector(page?.components);
  return { planTierComponent, ...useTiTiers(planTierComponent) };
};
